import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageHeaderComponent } from '../../../shared/page-header/page-header.component';
import { DetailsCardAction, DetailsCardContainerComponent } from '../../../shared/cards/details-card-container/details-card-container.component';
import { BehaviorSubject, filter } from 'rxjs';
import { PropertyManagementCompanyService } from '../../../../../services/property-management-company/property-management-company.service';
import { PropertyManagementCompanyDataService } from '../../../../../services/property-management-company/property-manager-company-data.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ScrollService } from '../../../../../services/scroll.service';
import { GrowlerService } from '../../../../../services/growler.service';
import { map, switchMap } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { CombineLinesListDirective, FullWidthLinesListDirective, LimitListItemsDirective, LoadingListDirective } from '../../../shared/directives/list.directive';
import { User } from '../../../../../models/entities/user';
import { LegfiPhonePipeModule } from '../../../../../pipes/format/legfi-phone.module';
import { OrganizationSidenavComponent, OrganizationSidenavInput, OrganizationSidenavOutput } from '../../../organizations/shared/organization-sidenav/organization-sidenav.component';
import { SidenavContentService } from '../../../shared/sidenav/sidenav-content/sidenav-content.service';
import { SimpleOrganization } from '../../../../../services/organization/organization.service';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { NgIf } from '@angular/common';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { MatDialogSizes } from '../../../../../enums/mat-dialog-sizes.enum';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterButtonValue } from '../../../../../models/entities/router-button-value';

@UntilDestroy()
@Component({
    selector: 'app-team-member-details',
    templateUrl: './team-member-details.component.html',
    standalone: true,
    imports: [
        PageHeaderComponent,
        DetailsCardContainerComponent,
        MatCardModule,
        MatProgressBarModule,
        MatListModule,
        CombineLinesListDirective,
        LoadingListDirective,
        LegfiPhonePipeModule,
        FullWidthLinesListDirective,
        LimitListItemsDirective,
        MatDialogModule,
        RouterLink,
        ButtonDirective,
        NgIf,
        MatButtonModule,
        MatIconModule,
    ],
})
export class TeamMemberDetailsComponent implements OnInit
{
    pageTitle: string;
    routerBackButton: RouterButtonValue;
    actions: DetailsCardAction[];
    lines = 1;
    loadingInitialData = true;

    user: User;
    organizationsList: SimpleOrganization[];

    readonly loadingSubject$ = new BehaviorSubject<boolean>(false);

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _dialog: MatDialog,
            private _sidenav: SidenavContentService,
            private _activatedRoute: ActivatedRoute,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    get isAuthUser() {
        const jwt = LegFiJwtService.read();
        return  this.user?.id === jwt.id;
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
        this.lines = this.getDetailActions().length;

        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/team`,
            ariaLabel: 'Go back to team',
        };

        this._activatedRoute.params.pipe(
                filter((params) => !!params.userId),
                map((params) => params.userId as number),
                switchMap((userId) => this.refreshTeamMember(userId)),
                untilDestroyed(this),
        ).subscribe({
            next: () => this.loadingInitialData = false,
            error: () => {
                this.loadingInitialData = false;
                this._growler.oops('Unable to load team member details');
            },
        });
    }

    private refreshTeamMember(userId: number) {
        return this._propertyManagementCompanyService.getPropertyManagementTeamMember(this.pmcId, userId).pipe(
                map((res) => {
                    this.user = res;
                    this.organizationsList = this.user.memberships
                            .filter((membership) => !!membership.organization)
                            .sort((a, b) => a.organization.name.localeCompare(b.organization.name))
                            .map((membership) => new SimpleOrganization(membership.organization));
                    this.pageTitle = this.user.name;
                    this.actions = this.getDetailActions();
                }),
        );
    }

    private openCommunityPermissions() {
        const data: OrganizationSidenavInput = {
            title: 'Manage Community Permissions',
            selectedOrganizationIds: this.organizationsList.map((organization) => organization.id),
            required: true,
        };

        this._sidenav.open(OrganizationSidenavComponent, {
            data,
            parentClass: 'selector-layout',
        }).afterClosed().subscribe((result: OrganizationSidenavOutput) => {
            if (result?.success) {
                const context = result.selectedCommunities.length === 1 ? 'Organization' : (result.selectedCommunities.length + ' Organizations');

                this.loadingSubject$.next(true);
                this._propertyManagementCompanyService.patchPropertyManagementTeamMember(this.pmcId, this.user.id, JSON.stringify({
                    selectedOrganizations: result.selectedCommunities.map((org) => org.id),
                })).pipe(untilDestroyed(this)).subscribe({
                    next: () => {
                        this._growler.success('Success', `Team member added to ${context}`);
                        this.organizationsList = result.selectedCommunities;
                        this.loadingSubject$.next(false);
                    },
                    error: () => {
                        this._growler.oops(`Unable to add team member ${context}`);
                        this.loadingSubject$.next(false);
                    },
                });
            }
        });
    }

    private togglePmcUser(isPropertyManager: boolean) {
        const data: ConfirmActionV2DialogInput = {
            title: isPropertyManager ? 'Change to Property Manager?' : 'Change to Community Manager?',
            content: isPropertyManager ? 'Add team member to property management dashboard.' : 'Remove team member from property management dashboard.',
            danger: isPropertyManager ? '' : 'This user will redirect to their community\'s on next login.',
            confirmButton: 'Yes, update user\'s access',
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.loadingSubject$.next(true);
                    this._propertyManagementCompanyService.patchPropertyManagementTeamMember(this.pmcId, this.user.id, JSON.stringify({
                        pmcUser: isPropertyManager,
                    })).pipe(
                            switchMap(() => this.refreshTeamMember(this.user.id)),
                            untilDestroyed(this),
                    ).subscribe({
                        next: () => {
                            this._growler.success('Success', `Team member access updated`);
                            this.loadingSubject$.next(false);
                        },
                        error: () => {
                            this._growler.oops(`Unable to update team member access`);
                            this.loadingSubject$.next(false);
                        },
                    });
                }
            },
        });
    }

    private getDetailActions(): DetailsCardAction[] {
        if (this.isAuthUser) {
            return [
                {
                    svg: 'v2:fi-br-building',
                    title: 'Manage Community Permissions',
                    action: 'community-permissions',
                    callback: () => this.openCommunityPermissions(),
                },
            ];
        }

        return [
            this.user?.propertyManagementCompanyUser ? {
                svg: 'v2:fi-br-user-permissions',
                title: `Change User to Community Manager`,
                description: 'Removes access to property management dashboard.',
                action: 'admin-permission',
                callback: () => this.togglePmcUser(false),
            } : {
                svg: 'v2:fi-br-user-permissions',
                title:  'Change Admin to Property Manager',
                description: 'Allows access to property management dashboard.',
                action: 'pm-permission',
                callback: () => this.togglePmcUser(true),
            },
            {
                svg: 'v2:fi-br-building',
                title: 'Manage Community Permissions',
                action: 'community-permissions',
                callback: () => this.openCommunityPermissions(),
            },
        ];
    }
}
